import React, {Component} from "react"
import {get} from "lodash"

import {RewardOrder, Recognition} from "@irati/types"
import authentication from "@irati/auth"
import {H3} from "@irati/ui/src/Headings"
import {Para, Strong} from "@irati/ui/src/Labels"
import Hr from "@irati/ui/src/Layout/Hr"
import {FormattedMessage, FormattedCurrency, FormattedDate, injectIntl, InjectIntl} from "@irati/utils/lang"
import {
  recognitionUsersToStringTitle,
  recognitionUsersToStringSubtitle
} from "@irati/recognition/utils/recognitionUsersToString"
import Button from "@irati/ui/src/Button"
import UsersTooltip from "@irati-web/recognition/src/components/shared/UsersTooltip"
import EndDate from "@irati-web/recognition/src/components/Popup/shared/layouts/EndDate"
import WhoCreated from "@irati-web/recognition/src/components/WhoCreated"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {pollUsersToStringTitle, pollUsersToStringSubtitle} from "@irati/recognition/utils/pollUsersToString"
import {getRewardType} from "@irati/reward/utils/whoRewarded/typeHelpers"

import ReceiptRow from "./Row"
import Message from "./Message"
import {Bold, LeftAlign, TopCard, GreyCard, InnerCard} from "./styled"

interface Props extends InjectIntl{
  buttonText: string,
  onClose: () => void,
  rewardOrder: RewardOrder
}

class InnerReceipt extends Component<Props> {

  getUsers = () => this.props.rewardOrder.recipients.results

  getUser = (recognition: Recognition) => {
    if (!recognition) { return null }

    if (!recognition.createdBy) {
      return authentication.user()
    }

    return recognition.createdBy
  }

  getTitle = () => {
    const {rewardOrder, formatMessage} = this.props

    if (rewardOrder.poll) {
      return pollUsersToStringTitle(this.getUsers(), formatMessage)
    }

    return recognitionUsersToStringTitle(this.getUsers(), formatMessage)
  }

  getSubtitle = () => {
    const {rewardOrder, formatMessage} = this.props
    const {poll, recognition} = rewardOrder

    if (poll) {
      return pollUsersToStringSubtitle(this.getUsers(), poll.title, formatMessage)
    }

    return recognitionUsersToStringSubtitle(this.getUsers(), firstnameLastname(this.getUser(recognition)), formatMessage)
  }

  getDate = () => {
    const {poll, recognition} = this.props.rewardOrder

    if (poll) {
      return poll.endsAt
    }

    return recognition.createdAt
  }

  getRewardHeader = () => {
    const {
      costPerUnit,
      reward,
      custom
    } = this.props.rewardOrder

    if (reward && costPerUnit) {
      return (
        <>
          <FormattedCurrency isCents={false} currency={costPerUnit.currency} value={costPerUnit.amount} /> {reward.header}
        </>
      )
    }

    return custom
  }

  getRewardVolume = () => {
    const {reward, quantity} = this.props.rewardOrder

    if (quantity > 1) {
      return `${reward.header} x${quantity}`
    }

    return reward.header
  }

  getAmountPaid = () => {
    const {rewardOrder: {product}, formatMessage} = this.props
    const vat = get(product, "product.vat")

    if (vat) {
      return formatMessage({id: "reward.receipt.row.amountPaidTaxPercentage"}, {percentage: vat})
    }

    return formatMessage({id: "reward.receipt.row.amountPaid"})
  }

  render() {
    const {rewardOrder, formatMessage, onClose, buttonText} = this.props
    const {
      costPerUnit,
      costTotal,
      poll,
      recognition,
      reward,
      updatedAt,
      uuid,
      createdBy
    } = rewardOrder

    const itemDetails = poll || recognition
    const title = this.getTitle()
    const subtitle = this.getSubtitle()
    const rewardType = getRewardType(reward, rewardOrder)

    return (
      <>
        <TopCard>
          <H3 small>
            <UsersTooltip placement="bottom" users={rewardOrder.recipients.results}>
              <Bold>{title}</Bold>
            </UsersTooltip> {subtitle}
          </H3>
          <Para center large flush>
            {this.getRewardHeader()}
          </Para>
          <Para blue center>
            {formatMessage({id: "reward.whoRewarded.addedBy"})} {firstnameLastname(createdBy)}
          </Para>
          {itemDetails &&
            <EndDate endsAt={this.getDate()} />
          }
          <LeftAlign>
            <Message poll={poll} recognition={recognition} />
            {itemDetails &&
              <WhoCreated createdBy={itemDetails.createdBy} />
            }
          </LeftAlign>
        </TopCard>
        <GreyCard>
          <Para><FormattedMessage id="reward.receipt.thankYou" /></Para>
          <FormattedMessage id="reward.receipt.recipients" /> <Strong>{firstnameLastname(rewardOrder.createdBy)}.</Strong>
          <InnerCard>
            <ReceiptRow
              leftText={
                formatMessage({id: "reward.receipt.row.order"}, {uuid})
              }
              rightText={
                <FormattedDate
                  year="numeric"
                  month="short"
                  day="2-digit"
                  value={updatedAt} />} />
            {reward && costPerUnit &&
            <>
              <Hr />
              <ReceiptRow
                rewardType={rewardType}
                leftText={this.getRewardVolume()}
                rightText={<FormattedCurrency isCents={false} currency={costPerUnit.currency} value={costPerUnit.amount} />} />
              </>
            }
            {costTotal &&
              <>
                <Hr />
                <ReceiptRow
                  leftText={this.getAmountPaid()}
                  rightText={<FormattedCurrency isCents={false} currency={costTotal.currency} value={costTotal.amount} />} />
              </>
            }
          </InnerCard>
          <Button large full primary onClick={onClose}>{buttonText}</Button>
        </GreyCard>
      </>
    )
  }
}

export default injectIntl(InnerReceipt)
